import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, CircularProgress, DialogActions, DialogContent,
  DialogTitle, IconButton, OutlinedInput, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import AppDialog from '../../../shared/components/AppDialog';
import LabeledField from '../../../shared/components/LabeledField';
import { applyApiFieldErrors } from '../../../shared/api';
import {
  DRIVER_INPUT_MODES,
  initialDriverInputMode,
  isDriverInputSubmittable,
  toDriverInputPayload,
} from '../model/driverInputForm';

export default function SeatSyncDialog({
  open,
  year,
  syncing,
  onClose,
  onSubmit,
  kind = 'seat',
  source = {},
}) {
  const { t } = useTranslation();
  const isCat8 = kind === 'cat8';
  const translationKey = isCat8 ? 'dataInput.cat8Sync' : 'dataInput.seatSync';
  const canSync = source.canSync !== false;
  const canManualInput = source.canManualInput === true;
  const [mode, setMode] = useState(initialDriverInputMode(canSync));
  const [asOfDate, setAsOfDate] = useState('');
  const [totalSeats, setTotalSeats] = useState('');
  const [workingDays, setWorkingDays] = useState('');
  const [totalAreaM2, setTotalAreaM2] = useState('');
  const [apiErrors, setApiErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const initialMode = initialDriverInputMode(canSync);
    setMode(initialMode);
    setAsOfDate(
      initialMode === DRIVER_INPUT_MODES.SYNC
        ? source.defaultSyncAsOfDate || ''
        : source.defaultManualAsOfDate || '',
    );
    setTotalSeats('');
    setWorkingDays('');
    setTotalAreaM2('');
    setApiErrors({});
  }, [canSync, open, source.defaultManualAsOfDate, source.defaultSyncAsOfDate]);

  const changeMode = (_event, nextMode) => {
    setMode(nextMode);
    setApiErrors({});
    setAsOfDate(
      nextMode === DRIVER_INPUT_MODES.SYNC
        ? source.defaultSyncAsOfDate || ''
        : source.defaultManualAsOfDate || '',
    );
  };

  const formValues = {
    mode,
    kind,
    asOfDate,
    totalSeats,
    workingDays,
    totalAreaM2,
    canSync,
    canManualInput,
  };
  const canSubmit = isDriverInputSubmittable(formValues);
  const isFixedManualDate = mode === DRIVER_INPUT_MODES.MANUAL
    && source.manualMinAsOfDate
    && source.manualMinAsOfDate === source.manualMaxAsOfDate;

  const clearApiError = (field) => {
    setApiErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submit = async () => {
    setApiErrors({});
    try {
      await onSubmit(toDriverInputPayload(formValues));
    } catch (error) {
      const nextErrors = {};
      const handled = applyApiFieldErrors(
        error,
        (field, detail) => { nextErrors[field] = detail.message; },
        t,
        {
          mapField: (field) => {
            const name = String(field || '').split('.').pop();
            const mapped = {
              as_of_date: 'asOfDate',
              requested_as_of_date: 'asOfDate',
              total_seats: 'totalSeats',
              working_days: 'workingDays',
              total_area_m2: 'totalAreaM2',
            }[name];
            if (mapped === 'asOfDate' && isCat8 && mode === DRIVER_INPUT_MODES.SYNC) {
              return null;
            }
            return mapped || null;
          },
        },
      );
      if (!handled) throw error;
      setApiErrors(nextErrors);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={syncing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {t(`${translationKey}.title`)}
        <IconButton
          onClick={onClose}
          disabled={syncing}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(`${translationKey}.description`, { year })}
          </Typography>
          {apiErrors['root.server'] && (
            <Alert severity="error">{apiErrors['root.server']}</Alert>
          )}
          <Tabs value={mode} onChange={changeMode} variant="fullWidth">
            <Tab
              value={DRIVER_INPUT_MODES.SYNC}
              label={t('dataInput.driverInput.syncTab')}
              disabled={!canSync || syncing}
            />
            <Tab
              value={DRIVER_INPUT_MODES.MANUAL}
              label={t('dataInput.driverInput.manualTab')}
              disabled={!canManualInput || syncing}
            />
          </Tabs>

          {!canSync && (
            <Alert severity="info">
              {t(`dataInput.driverInput.syncUnavailable.${source.syncUnavailableReason}`, {
                defaultValue: t('dataInput.driverInput.syncUnavailable.DEFAULT'),
              })}
            </Alert>
          )}

          {mode === DRIVER_INPUT_MODES.SYNC ? (
            <>
              {!isCat8 && (
                <LabeledField
                  label={t('dataInput.driverInput.asOfDate')}
                  htmlFor={`${kind}-sync-as-of-date`}
                  required
                  error={apiErrors.asOfDate}
                >
                  <OutlinedInput
                    id={`${kind}-sync-as-of-date`}
                    type="date"
                    size="small"
                    value={asOfDate}
                    onChange={(event) => {
                      setAsOfDate(event.target.value);
                      clearApiError('asOfDate');
                    }}
                    disabled={syncing}
                    error={Boolean(apiErrors.asOfDate)}
                    inputProps={{
                      min: source.manualMinAsOfDate || undefined,
                      max: source.manualMaxAsOfDate || undefined,
                      readOnly: source.manualMinAsOfDate === source.manualMaxAsOfDate,
                    }}
                  />
                </LabeledField>
              )}
              {isCat8 && source.defaultSyncAsOfDate && (
                <Typography variant="body2" color="primary.main" fontWeight={700}>
                  {t('dataInput.driverInput.captureDate', {
                    date: source.defaultSyncAsOfDate,
                  })}
                </Typography>
              )}
            </>
          ) : (
            <>
              <LabeledField
                label={t('dataInput.driverInput.effectiveDate')}
                htmlFor={`${kind}-manual-as-of-date`}
                required
                error={apiErrors.asOfDate}
              >
                <OutlinedInput
                  id={`${kind}-manual-as-of-date`}
                  type="date"
                  size="small"
                  value={asOfDate}
                  onChange={(event) => {
                    setAsOfDate(event.target.value);
                    clearApiError('asOfDate');
                  }}
                  disabled={syncing}
                  error={Boolean(apiErrors.asOfDate)}
                  inputProps={{
                    min: source.manualMinAsOfDate || undefined,
                    max: source.manualMaxAsOfDate || undefined,
                    readOnly: Boolean(isFixedManualDate),
                  }}
                />
              </LabeledField>
              {isCat8 ? (
                <LabeledField
                  label={t('dataInput.driverInput.totalAreaM2')}
                  htmlFor="cat8-manual-total-area"
                  required
                  error={apiErrors.totalAreaM2}
                >
                  <OutlinedInput
                    id="cat8-manual-total-area"
                    type="number"
                    size="small"
                    value={totalAreaM2}
                    onChange={(event) => {
                      setTotalAreaM2(event.target.value);
                      clearApiError('totalAreaM2');
                    }}
                    disabled={syncing}
                    error={Boolean(apiErrors.totalAreaM2)}
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </LabeledField>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <LabeledField
                    label={t('dataInput.driverInput.totalSeats')}
                    htmlFor="seat-manual-total-seats"
                    required
                    sx={{ flex: 1 }}
                    error={apiErrors.totalSeats}
                  >
                    <OutlinedInput
                      id="seat-manual-total-seats"
                      type="number"
                      size="small"
                      value={totalSeats}
                      onChange={(event) => {
                        setTotalSeats(event.target.value);
                        clearApiError('totalSeats');
                      }}
                      disabled={syncing}
                      error={Boolean(apiErrors.totalSeats)}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </LabeledField>
                  <LabeledField
                    label={t('dataInput.driverInput.workingDays')}
                    htmlFor="seat-manual-working-days"
                    required
                    sx={{ flex: 1 }}
                    error={apiErrors.workingDays}
                  >
                    <OutlinedInput
                      id="seat-manual-working-days"
                      type="number"
                      size="small"
                      value={workingDays}
                      onChange={(event) => {
                        setWorkingDays(event.target.value);
                        clearApiError('workingDays');
                      }}
                      disabled={syncing}
                      error={Boolean(apiErrors.workingDays)}
                      inputProps={{ min: 0, max: 366, step: 1 }}
                    />
                  </LabeledField>
                </Stack>
              )}
            </>
          )}

        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose} disabled={syncing}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={syncing || !canSubmit}
          startIcon={syncing ? <CircularProgress color="inherit" size={15} /> : undefined}
        >
          {syncing
            ? t('dataInput.actions.syncing')
            : t(mode === DRIVER_INPUT_MODES.SYNC
              ? 'dataInput.actions.sync'
              : 'dataInput.driverInput.saveManual')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
